// Inline receipt-link popover for the Inventory Changelog (slice 4f).
// Opens from a changelog row's "Link" button when the entry has no
// receipt yet (or "Change" when one's already linked). Shows the most
// recent receipts in a searchable list + a "Clear link" option for
// already-linked entries. On select, calls `linkCostEntryToReceipt`
// and asks the parent to refresh.
//
// Why a centred modal rather than an actual anchored popover: every
// existing manufacturing-panel disclosure (upload form, extraction
// modal, etc.) uses the same fixed/centred chrome, so this stays
// visually consistent and dodges the per-target absolute-position
// math an anchored popover would need.
import { useEffect, useMemo, useState } from "react";
import { mfgModalShellClass, mfgXsSecondaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  linkCostEntryToReceipt,
  listManufacturingReceipts,
} from "@/lib/manufacturingClient";
import type {
  MfgInventoryChangelogRow,
  MfgInventoryReceipt,
} from "@/lib/manufacturing/types";

type Props = {
  row: MfgInventoryChangelogRow;
  onClose: () => void;
  /** Called after a successful link/unlink so the parent list can
   *  refresh and pick up the new chip / vendor name. */
  onLinked?: () => void;
};

const dangerBtnClass =
  "rounded-md border border-red-200 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-40 dark:border-red-800 dark:bg-zinc-900 dark:text-red-300 dark:hover:bg-red-950/40";

export function ManufacturingChangelogReceiptLinker({ row, onClose, onLinked }: Props) {
  const [receipts, setReceipts] = useState<MfgInventoryReceipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const r = await listManufacturingReceipts({ limit: 30 });
      if (cancelled) return;
      setLoading(false);
      if (!r.ok) {
        setError(r.error);
        return;
      }
      setReceipts(r.data.receipts);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return receipts;
    return receipts.filter((r) => {
      const v = (r.vendor_name ?? "").toLowerCase();
      const d = (r.receipt_date ?? "").toLowerCase();
      const p = (r.plant_code ?? "").toLowerCase();
      return v.includes(q) || d.includes(q) || p.includes(q);
    });
  }, [receipts, search]);

  async function setLink(receiptId: string | null) {
    setWorking(true);
    setError(null);
    const r = await linkCostEntryToReceipt(row.id, receiptId);
    setWorking(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    onLinked?.();
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:items-center"
      onClick={onClose}
    >
      <div
        className={`max-w-md p-4 ${mfgModalShellClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
              Link to receipt
            </h3>
            <p className="mt-0.5 text-[11px] text-gray-500 dark:text-zinc-400">
              {row.material_name ?? "(unknown material)"} ·{" "}
              {Number(row.quantity).toFixed(2)} {row.material_unit ?? ""} @ $
              {Number(row.unit_cost).toFixed(2)}
            </p>
          </div>
          <button type="button" onClick={onClose} className={mfgXsSecondaryBtnClass}>
            Close
          </button>
        </header>

        <input
          autoFocus
          placeholder="Filter by vendor, date, plant"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mt-3 w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-900 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
        />

        {error ? (
          <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-2 py-1 text-[11px] text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
            {error}
          </div>
        ) : null}

        <div className="mt-2 max-h-72 overflow-y-auto rounded-lg border border-gray-100 dark:border-zinc-800">
          {loading ? (
            <div className="px-3 py-4 text-center text-xs text-gray-500 dark:text-zinc-400">
              Loading receipts…
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-gray-500 dark:text-zinc-400">
              No receipts match.
            </div>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
              {filtered.map((r) => {
                const isCurrent = row.receipt_id === r.id;
                return (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => void setLink(r.id)}
                      disabled={working || isCurrent}
                      className={`flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-left text-xs hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-60 dark:hover:bg-indigo-950/30 ${
                        isCurrent ? "bg-emerald-50/60 dark:bg-emerald-950/30" : ""
                      }`}
                    >
                      <span className="min-w-0 truncate">
                        <span className="font-medium text-gray-900 dark:text-zinc-100">
                          {r.vendor_name || "(no vendor)"}
                        </span>{" "}
                        <span className="text-gray-500 dark:text-zinc-400">
                          · {r.receipt_date}
                          {r.plant_code ? ` · ${r.plant_code}` : ""}
                        </span>
                      </span>
                      <span className="tabular-nums text-gray-600 dark:text-zinc-400">
                        ${Number(r.total_cost).toFixed(2)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {row.receipt_id ? (
          <div className="mt-3 flex items-center justify-end">
            <button
              type="button"
              onClick={() => void setLink(null)}
              disabled={working}
              className={dangerBtnClass}
            >
              {working ? "Clearing…" : "Clear link"}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
